import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import type { LegacyCaseDetail } from "../../../types/legacyCases";
import type { LegacyReviewForm } from "../../../utils/legacyReviewForm";
import { useLegacyPhysicians } from "../../../hooks/useLegacyCases";

interface Props {
  form: LegacyReviewForm;
  detail: LegacyCaseDetail;
}

export default function LegacyCaseFields({ form, detail }: Props) {
  const [search, setSearch] = useState("");
  const [query] = useDebouncedValue(search, 300);
  const physicians = useLegacyPhysicians(detail.id, query);
  const [selected, setSelected] = useState(
    detail.physicianCandidates.find(
      (physician) => physician.id === form.values.physicianId
    )
  );
  const candidates = useMemo(
    () =>
      Array.from(
        new Map(
          [
            ...detail.physicianCandidates,
            ...(physicians.data ?? []),
            ...(selected ? [selected] : []),
          ].map((physician) => [physician.id, physician])
        ).values()
      ),
    [detail.physicianCandidates, physicians.data, selected]
  );
  const options = useMemo(
    () =>
      candidates
        .map((physician) => ({
          value: physician.id,
          label: `${physician.lastName}, ${physician.firstName}${
            physician.npi ? ` · NPI ${physician.npi}` : ""
          }`,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [candidates]
  );
  const handleSearch = useCallback((value: string) => setSearch(value), []);
  const handleSelect = useCallback(
    (value: string | null) => {
      form.setFieldValue("physicianId", value || "");
      setSelected(candidates.find((physician) => physician.id === value));
    },
    [candidates, form]
  );

  return (
    <Stack gap="md">
      <Title order={3}>Case and referring physician</Title>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <DateInput
          label="Date received"
          withAsterisk
          aria-required
          valueFormat="MM/DD/YYYY"
          clearable
          {...form.getInputProps("dateReceived")}
        />
        <DateInput
          label="Date collected"
          valueFormat="MM/DD/YYYY"
          clearable
          {...form.getInputProps("dateCollected")}
        />
        <DateInput
          label="Original reported date"
          valueFormat="MM/DD/YYYY"
          clearable
          {...form.getInputProps("reportedDate")}
        />
      </SimpleGrid>
      <Radio.Group
        label="Referring physician"
        {...form.getInputProps("physicianAction")}
      >
        <Stack gap="xs" mt="xs">
          <Radio value="unmatched" label="Leave unmatched" />
          <Radio value="match" label="Match an existing physician" />
        </Stack>
      </Radio.Group>
      {form.values.physicianAction === "match" && (
        <Stack gap="sm">
          <Select
            label="Existing physician"
            withAsterisk
            aria-required
            searchable
            clearable
            data={options}
            searchValue={search}
            onSearchChange={handleSearch}
            nothingFoundMessage={
              query.length < 2
                ? "Type at least 2 characters to search"
                : "No matching physicians"
            }
            {...form.getInputProps("physicianId")}
            onChange={handleSelect}
            comboboxProps={{
              withinPortal: true,
              position: "bottom-start",
              offset: 8,
            }}
          />
          {physicians.isFetching && (
            <Text size="sm" role="status">
              Searching physicians…
            </Text>
          )}
          {physicians.error && (
            <Alert color="red" title="Physician search unavailable">
              {physicians.error.message}
            </Alert>
          )}
        </Stack>
      )}
    </Stack>
  );
}
